'use client';

import type { ErrorLogLevel, ErrorLogSource } from './types';

export interface ErrorReportPayload {
  level: ErrorLogLevel;
  source: ErrorLogSource;
  context: string;
  message: string;
  stack?: string;
  url?: string;
  userAgent?: string;
  metadata?: Record<string, unknown>;
}

const QUEUE: ErrorReportPayload[] = [];
let flushing = false;
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let registered = false;
const FLUSH_INTERVAL_MS = 2500;
const MAX_QUEUE = 50;
const MAX_RETRIES = 3;
const retries = new WeakMap<ErrorReportPayload, number>();
const DEDUPE_WINDOW_MS = 4000;
const recentSignatures = new Map<string, number>();
const EXTENSION_SKIP_THRESHOLD = 25;
const extensionSkipSamples: Array<{
  message: string;
  filename?: string;
  ts: number;
}> = [];
let extensionSkipCount = 0;

function buildSignature(payload: ErrorReportPayload): string {
  return `${payload.level}|${payload.source}|${payload.context}|${payload.message.slice(0, 200)}`;
}

function shouldDedupe(payload: ErrorReportPayload): boolean {
  const now = Date.now();
  const sig = buildSignature(payload);
  const last = recentSignatures.get(sig);
  if (last && now - last < DEDUPE_WINDOW_MS) return true;
  recentSignatures.set(sig, now);
  if (recentSignatures.size > 200) {
    const cutoff = now - DEDUPE_WINDOW_MS * 4;
    for (const [k, t] of recentSignatures) {
      if (t < cutoff) recentSignatures.delete(k);
    }
  }
  return false;
}

export function reportError(payload: ErrorReportPayload): void {
  if (typeof window === 'undefined') return;
  if (shouldDedupe(payload)) return;
  QUEUE.push({
    ...payload,
    message: payload.message.slice(0, 4096),
    stack: payload.stack?.slice(0, 12_000),
    userAgent: payload.userAgent ?? navigator.userAgent,
    url: payload.url ?? window.location.pathname,
  });
  if (QUEUE.length > MAX_QUEUE) QUEUE.shift();
  scheduleFlush();
}

function scheduleFlush(): void {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    void flushQueue();
  }, FLUSH_INTERVAL_MS);
}

async function flushQueue(): Promise<void> {
  if (flushing) return;
  if (QUEUE.length === 0) return;
  flushing = true;
  const batch = QUEUE.splice(0, QUEUE.length);
  try {
    const cookieName =
      document.querySelector('meta[name="kontora-csrf-cookie"]')?.getAttribute('content') ??
      'kontora_csrf';
    const cookie = document.cookie
      .split(';')
      .map((entry) => entry.trim())
      .find((entry) => entry.startsWith(cookieName + '='));
    if (!cookie) return;
    const csrfToken = decodeURIComponent(cookie.slice(cookieName.length + 1));
    // Each payload is sent individually because the BFF ingest endpoint
    // currently accepts a single record per request. Keepalive ensures the
    // request survives page unload.
    for (const payload of batch) {
      try {
        const response = await fetch('/api/logs/ingest', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Kontora-CSRF-Token': csrfToken },
          body: JSON.stringify(payload),
          keepalive: true,
          credentials: 'same-origin',
        });
        if (!response.ok && (response.status >= 500 || response.status === 429))
          throw new Error('Log intake unavailable');
      } catch {
        // Restore single payload to queue on transient failure.
        const attempts = (retries.get(payload) ?? 0) + 1;
        retries.set(payload, attempts);
        if (attempts < MAX_RETRIES && QUEUE.length < MAX_QUEUE) QUEUE.push(payload);
      }
    }
  } finally {
    flushing = false;
    if (QUEUE.length > 0) scheduleFlush();
  }
}

function isExtensionError(event: ErrorEvent | PromiseRejectionEvent): boolean {
  const errEvent = event as ErrorEvent;
  const filename = errEvent.filename ?? '';
  if (
    filename.startsWith('chrome-extension://') ||
    filename.startsWith('moz-extension://') ||
    filename.startsWith('safari-extension://') ||
    filename.startsWith('extension://') ||
    filename.includes('injected.bundle.js')
  ) {
    return true;
  }
  const reason = (event as PromiseRejectionEvent).reason;
  const stack = reason instanceof Error ? (reason.stack ?? '') : '';
  const message = reason instanceof Error ? reason.message : String(reason ?? '');
  if (
    stack.includes('chrome-extension://') ||
    stack.includes('moz-extension://') ||
    stack.includes('safari-extension://') ||
    stack.includes('extension://') ||
    stack.includes('injected.bundle.js') ||
    message.includes('injected.bundle.js')
  ) {
    return true;
  }
  return false;
}

function recordExtensionSkip(event: ErrorEvent | PromiseRejectionEvent): void {
  extensionSkipCount += 1;
  if (extensionSkipCount <= EXTENSION_SKIP_THRESHOLD) {
    const errEvent = event as ErrorEvent;
    const reason = (event as PromiseRejectionEvent).reason;
    const message =
      errEvent.message ?? (reason instanceof Error ? reason.message : String(reason ?? ''));
    extensionSkipSamples.push({
      message: message.slice(0, 200),
      filename: errEvent.filename,
      ts: Date.now(),
    });
    if (typeof console !== 'undefined' && console.debug) {
      console.debug('[error-reporter] suppressed browser-extension error', {
        message,
        filename: errEvent.filename,
        totalSkipped: extensionSkipCount,
      });
    }
  }
}

/**
 * Registers the global error handlers. Idempotent.
 * Called from the Next.js instrumentation-client.ts entrypoint.
 */
export function registerErrorReporter(): void {
  if (registered) return;
  if (typeof window === 'undefined') return;
  registered = true;

  window.addEventListener(
    'error',
    (event) => {
      if (isExtensionError(event)) {
        recordExtensionSkip(event);
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }
      reportError({
        level: 'error',
        source: 'frontend',
        context: 'window.error',
        message: event.message || 'Unbekannter Fehler',
        stack: event.error instanceof Error ? event.error.stack : undefined,
        metadata: {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
        },
      });
    },
    true,
  );

  window.addEventListener(
    'unhandledrejection',
    (event) => {
      const reason = event.reason;
      if (isExtensionError(event as PromiseRejectionEvent)) {
        recordExtensionSkip(event as unknown as ErrorEvent);
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }
      reportError({
        level: 'error',
        source: 'frontend',
        context: 'unhandledrejection',
        message: reason instanceof Error ? reason.message : String(reason),
        stack: reason instanceof Error ? reason.stack : undefined,
      });
    },
    true,
  );

  window.addEventListener('beforeunload', () => {
    // Best-effort flush on navigation; uses keepalive fetch inside flushQueue.
    void flushQueue();
  });
}

// Auto-register on import so legacy code that just imports the module
// continues to work. The instrumentation-client.ts also calls it explicitly.
registerErrorReporter();
